import type {
  IMfaAddTotpStartView,
  IMfaAddTotpVerifyView,
  IMfaPasskeyOptionsView,
  IMfaPasskeyView,
  IMfaStatusView,
} from '@shared/services';
import { authApi } from '../../client';

/**
 * Session-authorized MFA management requests for the Security page. Unlike the login-time
 * `mfa.request.ts` (which rides an `mfaPending` bearer over `publicApi`), every call here goes over
 * `authApi` — the SDK's axios instance that carries the adopted access token — because the user is
 * already signed in and managing their own factors. Backed by `MfaManagementController`
 * (`@UsersCommonAccess()`): status is passkey-inclusive, and passkey add/remove issue NO new session.
 */

export interface AddPasskeyVerifyParams {
  /** The browser's `RegistrationResponseJSON`, JSON-stringified. */
  response: string;
  /** Optional human label so a person with several passkeys can tell them apart. */
  label?: string;
}

export interface RemovePasskeyParams {
  /** The passkey's stable id (`IMfaPasskeyView.credentialId`). */
  credentialId: string;
}

export interface MfaStepUpParams {
  /**
   * The account's current password. A mandatory step-up (`MfaStepUpDto`, `@IsNotEmpty`) in front of
   * every factor-enrollment start call; the gateway injects the bearer, so only the password travels.
   */
  currentPassword: string;
}

export interface MfaAddCodeParams {
  /** The one-time code from the authenticator app (TOTP) or the emailed code, confirming enrollment. */
  code: string;
}

/** Read the account's current MFA factors (TOTP/email flags plus the registered passkeys). */
export async function getMfaStatus(): Promise<IMfaStatusView> {
  const { data } = await authApi.get('/api/v1/auth/mfa/status');

  return data;
}

/**
 * Start a passkey registration ceremony; returns WebAuthn creation options as a JSON string. The
 * gateway now requires the step-up `currentPassword` here (`MfaStepUpDto`) — omitting it answers 400.
 */
export async function addPasskeyOptions({ currentPassword }: MfaStepUpParams): Promise<IMfaPasskeyOptionsView> {
  const { data } = await authApi.post('/api/v1/auth/mfa/passkey/add/options', { currentPassword });

  return data;
}

/**
 * Start a session-authorized authenticator (TOTP) enrollment. After the step-up password is accepted,
 * returns the setup material — the shared secret and provisioning URI — to display as a QR / manual key.
 */
export async function addTotpStart({ currentPassword }: MfaStepUpParams): Promise<IMfaAddTotpStartView> {
  const { data } = await authApi.post('/api/v1/auth/mfa/totp/add/start', { currentPassword });

  return data;
}

/**
 * Confirm a session-authorized TOTP enrollment with a code from the authenticator app. Returns the
 * one-time backup codes (shown once; no session is issued — the user keeps their existing session).
 */
export async function addTotpVerify({ code }: MfaAddCodeParams): Promise<IMfaAddTotpVerifyView> {
  const { data } = await authApi.post('/api/v1/auth/mfa/totp/add/verify', { code });

  return data;
}

/**
 * Start a session-authorized email one-time-code enrollment. After the step-up password is accepted,
 * the backend sends a code to the account email; the response carries no body.
 */
export async function addEmailStart({ currentPassword }: MfaStepUpParams): Promise<void> {
  await authApi.post('/api/v1/auth/mfa/email/add/start', { currentPassword });
}

/** Confirm a session-authorized email enrollment with the emailed code. No body on success. */
export async function addEmailVerify({ code }: MfaAddCodeParams): Promise<void> {
  await authApi.post('/api/v1/auth/mfa/email/add/verify', { code });
}

/**
 * Complete a passkey registration with the browser's stringified WebAuthn response. Returns the
 * newly registered passkey (no session tokens — the user keeps their existing session).
 */
export async function addPasskeyVerify({ response, label }: AddPasskeyVerifyParams): Promise<IMfaPasskeyView> {
  // Omit `label` when unset so the backend keeps its null-label default rather than storing an empty string.
  const { data } = await authApi.post('/api/v1/auth/mfa/passkey/add/verify', {
    response,
    ...(label ? { label } : {}),
  });

  return data;
}

/** Remove one registered passkey by its credential id. Removal is unconditional (no keep-one guard). */
export async function removePasskey({ credentialId }: RemovePasskeyParams): Promise<void> {
  await authApi.delete(`/api/v1/auth/mfa/passkey/${encodeURIComponent(credentialId)}`);
}

/**
 * Disable the account's code factor. Unlike the enrollment-start calls there is no `currentPassword`
 * step-up: the request takes no body and answers 204. The backend hard-deletes the account's single
 * code-factor enrollment (TOTP or email — whichever is enrolled), including its backup codes.
 */
export async function disableMfa(): Promise<void> {
  await authApi.post('/api/v1/auth/mfa/disable');
}
